import React from 'react';
import styled, { css } from 'styled-components';
import { format } from 'rut.js';
import { number, string, oneOfType, func, bool, any, object } from 'prop-types';
import { isEmpty, replace } from 'lodash';
import { FlexContainer } from '../../layouts/Wrapper/Base.components';
import { withTranslation } from '../../../hoc';
import { formatPrice } from '../../pdf-commons/utils';
import ComponentLoading from '../Loading/ComponentLoading';
import { Column, Icon, Text } from '../../common';
import { GRAY_DARK } from '../../../utils/colors';

const InputContainer = styled(FlexContainer)`
  width: 100%;
  background: transparent;
  ${({ width }) => width && css`width: ${width};`}
  ${({ layout = 'column' }) => {
        if (layout === 'row') return css`margin: 0px 10px;`;
        return css`margin: 10px 0px;`;
    }}

`;
const Label = styled.label`
  color: ${({ color = '#333333' }) => color};
  margin-right: 10px;
  font-size: ${({ size = 12 }) => size}px;
  font-weight: ${({ weight = '600' }) => weight};
  font-style: normal;
  line-height: 1.75;
  font-stretch: normal;
  letter-spacing: normal;

  ${props => props.error && `
    color: #d60101;
  `}
`;

const TextArea = styled.textarea`
  width: 100%;
  ${({ height }) => height && css`height: ${height}px;`}
  outline: none;
  border-radius: 4px;
  padding: 10px;
  font-size: 12px;
  color: #848484;
  border: 1px solid #eaeaea;

  :focus {
    border: 1px solid #848484;
  }
`;

const Input = styled.input`
  border-radius: 4px;
  background-color: ${({ backgroundColor = '#fffff' }) => backgroundColor};
  outline: none;
  border: solid 1px ${({ borderColor }) => borderColor || GRAY_DARK};
  width: 100%;
  height: 34px;
  padding: 10px;
  ${({ codePrefix }) => codePrefix && css`
    padding: 10px 10px 10px 30px;
  `}
  font-size: 12px;
  color: ${({ disabled }) => disabled ? '#848484' : '#333333'};
  ${({ disabled }) => disabled && css`
    cursor: not-allowed;
  `}

  :focus {
    border: 1px solid #848484;
  }

  ${({ success }) => {
        const isSuccess = success;
        const successColor = '#4cb358';
        return isSuccess && css`
        border: solid 1px ${successColor};

        &:focus {
          box-shadow: 0 0 2pt 1pt ${successColor};
        }
      
        &:blur {
          box-shadow: 0 0 2pt 1pt ${successColor};
        }
    `;
    }}
  ${({ error }) => {
        const isError = error;
        const errorColor = '#d60101';
        return isError && css`
      color: #d60101;
      border: solid 1px ${errorColor};

      &:focus {
        box-shadow: 0 0 2pt 1pt ${errorColor};
      }
    
      &:blur {
        box-shadow: 0 0 2pt 1pt ${errorColor};
      }
    `;
    }}
  ::-webkit-inner-spin-button, ::-webkit-outer-spin-button { 
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0; 
}
`;

const CodePrefix = styled.span`
  background-color: transparent;
  position: absolute;
  left: 5px;
  font-size: 12px;
  color: #848484;
  padding: 2px 0 0 0;
`;

const EditWrapper = styled.div`
  cursor: pointer;
  position: absolute;
  display: flex;
  height: 100%;
  justify-content flex-end;
  align-items: center;
  right: 10px;
`;

const Help = withTranslation(({ errorLabel, children }) => {
    const HelpContent = styled.span`
      color: black;
      font-size: 11px;
      padding: 5px 0px;
      transition: visibility 0s, opacity 0.5s linear;
      ${props => props.error && `
        color: #d60101;
      `}

      ${props => props.success && `
        border: solid 1px #4cb358;
      `}
  `;
    return (
        <HelpContent error={errorLabel}>
            {errorLabel || children}
        </HelpContent>
    );
}, ['errorLabel']);

const FormInput = ({
    layout,
    labelSize,
    placeholder,
    type,
    direction,
    name,
    value,
    error,
    success,
    label,
    labelColor,
    labelWeight,
    onChange,
    onMouseUp,
    onKeyPress,
    onBlur,
    textArea,
    heightTextArea,
    disabled,
    maxLength,
    isPrice,
    codePrefix,
    isDocumentId,
    borderColor,
    iconLeft,
    onClickLeftIcon,
    leftIconColor,
    showLeftIcon,
    sizeIconleft,
    loading,
    onlyNumbers,
    onlyLetters,
    noSpecialCharacters,
    noSpaces,
    width,
    style,
    id,
    backgroundColor,
    footerNote }) => {
    const _formatValue = (value) => {
        const phoneRegExp = /[^\d.]/g;
        if (isPrice) return formatPrice(value);
        if (codePrefix) return String(value).replace(new RegExp(phoneRegExp), '').replace('.', '');
        if (isDocumentId) return isEmpty(value) ? value : format(value);
        if (onlyNumbers) return replace(parseInt(value), /\D/g, '');
        if (onlyLetters) return replace(value, /[^a-zA-Z]+/g, '');
        if (noSpecialCharacters && noSpaces) return replace(replace(value, /[^\w\s]/gi, ''), ' ', '');
        if (noSpaces) return replace(value, /^\S*$/, '');
        if (noSpecialCharacters) return replace(value, /[^\w\s]/gi, '');
        return value;
    };

    if (loading) return <ComponentLoading />;

    return (
        <InputContainer direction='column' style={style} layout={layout} width={width}>
            <FlexContainer direction={direction}>
                <Label error={error} success={success} size={labelSize} color={labelColor} weight={labelWeight}>{label}</Label>
                {
          textArea ? <>
              <TextArea
                  placeholder={placeholder}
                  type={type}
                  name={name}
                  error={error}
                  success={success}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={disabled}
                  maxLength={maxLength}
                  height={heightTextArea}
              />
              {footerNote && <Text text='Para incluir un nuevo párrafo incluye esto al final de la frase \n' size={10} />}
          </> : (
              <Column justify='center' noFlex style={{ position: 'relative' }}>
                  { codePrefix && <CodePrefix>{codePrefix}</CodePrefix> }
                  <Input
                      id={id}
                      placeholder={placeholder}
                      type={type}
                      name={name}
                      error={error}
                      success={success}
                      value={_formatValue(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      disabled={disabled}
                      codePrefix={codePrefix}
                      maxLength={codePrefix ? 9 : maxLength}
                      borderColor={borderColor}
                      onKeyPress={onKeyPress}
                      onMouseUp={onMouseUp}
                      backgroundColor={backgroundColor}
                />
                  { iconLeft && showLeftIcon && (
                  <EditWrapper>
                      <Icon color={leftIconColor} name={iconLeft} onClick={onClickLeftIcon} size={sizeIconleft} />
                  </EditWrapper>
                  )}
              </Column>
          )
        }
            </FlexContainer>
            { error && (error && typeof error === 'string' ? <Help errorLabel={error} /> : <Help >{error} </Help>) }
        </InputContainer>
    );
};

const FormikInput = ({ field, form: { errors, dirty, touched, ...form }, ...rest }) => {
    return (
        <FormInput {...field} error={(touched[field.name]) && errors[field.name]} {...rest} />
    );
};

FormikInput.propTypes = {
    form: object,
    field: object
};

FormInput.propTypes = {
    id: string,
    codePrefix: string,
    isDocumentId: bool,
    direction: string,
    label: string,
    labelSize: oneOfType([number, string]),
    labelWeight: oneOfType([number, string]),
    labelColor: string,
    layout: string,
    error: any,
    isPrice: bool,
    success: bool,
    name: string,
    maxLength: number,
    type: string,
    onChange: func,
    onMouseUp: func,
    onKeyPress: func,
    onBlur: func,
    placeholder: string,
    value: string,
    style: object,
    disabled: bool,
    textArea: bool,
    footerNote: bool,
    minHeightTextArea: number
};

FormInput.defaultProps = {
    direction: 'column',
    style: {},
    field: {},
    disabled: false,
    textArea: false,
    maxLength: 240,
    onKeyPress: () => {},
    onMouseUp: () => {},
    footerNote: false,
    heightTextArea: 100
};

export { FormInput, FormikInput };
